import React from "react";
import styled from "styled-components";
import useTheme from "../../lib/useTheme";
import { mergeStrings } from "../../lib/helpers";
import Dot from "./Dot";
import Arrow from "./Arrow";

interface DGA_CarouselProps {
  style?: "arrows" | "dots" | "dotsOnly";
  dotsSize?: "large" | "medium" | "small";
  content: React.ReactNode[];
  selectedContentIndex?: number;
  className?: string;
  onChange?: (dotIndex: number) => void;
  arrowsSize: Size;
  arrowsStyle: "primary" | "subtle";
}

const Carousel: React.FC<DGA_CarouselProps> = ({
  style = "dots",
  selectedContentIndex,
  content,
  dotsSize = "medium",
  className,
  onChange,
  arrowsSize = "medium",
  arrowsStyle = "subtle",
}) => {
  const theme = useTheme();

  const onChangeHandler = (i: number) => {
    onChange?.(i);
  };

  React.useEffect(() => {
    if (style !== "dotsOnly") {
      const slides = document.querySelectorAll(".dgaui_carouselSlide");
      const targetSlide = slides[selectedContentIndex!] as HTMLDivElement;
      const carouselContainer = document.querySelector(".dgaui_carouselSlides");

      if (targetSlide && carouselContainer) {
        const scrollPosition = targetSlide.offsetLeft;

        carouselContainer.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        });
      }
    }
  }, [selectedContentIndex, style]);

  // Initially
  React.useEffect(() => {
    if (style !== "dotsOnly") {
      const slides = document.querySelectorAll(".dgaui_carouselSlide");
      const targetSlide = slides[selectedContentIndex!] as HTMLDivElement;
      const carouselContainer = document.querySelector(".dgaui_carouselSlides");

      if (targetSlide && carouselContainer) {
        const scrollPosition = targetSlide.offsetLeft;

        carouselContainer.scrollTo({
          left: scrollPosition,
          behavior: "instant",
        });
      }
    }
  }, []);

  return (
    <StyledComponent
      $theme={theme}
      className={mergeStrings("dgaui dgaui_carousel", className)}
      $arrowsSize={arrowsSize}
      $style={style}
    >
      {style !== "dotsOnly" && (
        <div className={"dgaui_carouselSlidesContainer " + (style === "arrows" ? " arrows" : "")}>
          <div className="leftArrow">
            <Arrow
              position="left"
              style={arrowsStyle}
              size={arrowsSize}
              onClick={() => selectedContentIndex! > 0 && onChange?.(selectedContentIndex! - 1)}
            />
          </div>
          <div className="dgaui_carouselSlides">
            {content.map((el, i) => (
              <div key={i} className="dgaui_carouselSlide">
                {el}
              </div>
            ))}
          </div>
          <div className="rightArrow">
            <Arrow
              position="right"
              style={arrowsStyle}
              size={arrowsSize}
              onClick={() =>
                selectedContentIndex! < content.length - 1 && onChange?.(selectedContentIndex! + 1)
              }
            />
          </div>
        </div>
      )}

      {(style === "dots" || style === "dotsOnly") && (
        <div className="dots">
          {content.map((_, i) => (
            <Dot selected={i === selectedContentIndex} size={dotsSize} onClick={() => onChange?.(i)} />
          ))}
        </div>
      )}
    </StyledComponent>
  );
};

const StyledComponent = styled.div<{
  $theme: Theme;
  $arrowsSize: Size;
  $style: "arrows" | "dots" | "dotsOnly";
}>`
  direction: ${(p) => p.$theme.direction};
  height: 100%;
  width: 100%;

  .dgaui_carouselSlidesContainer {
    .leftArrow,
    .rightArrow {
      display: none;
    }
    &.arrows {
      display: flex;
      .dgaui_carouselSlides {
        flex: 1;
      }
      .leftArrow,
      .rightArrow {
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .leftArrow {
        margin-right: 44px;
        margin-right: 44px;
      }
      .rightArrow {
        margin-left: 44px;
      }
    }
  }

  .dgaui_carouselSlides {
    overflow-x: hidden;
    overscroll-behavior-x: contain; /* Prevents unwanted page scrolling */
    scroll-snap-type: x mandatory; /* Snaps to each slide */
    height: 100%;
    display: block;
    margin-bottom: ${(p) => (p.$style === "arrows" ? 0 : 24)}px;
    display: flex;

    .dgaui_carouselSlide {
      flex: 0 0 100%;
      width: 100%; /* Fallback for older browsers */
      /* Aligns the snap point to the start of each slide */
      scroll-snap-align: start;
    }
  }

  .dots {
    padding: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .dgaui_button {
    border-radius: 9999px;
    border-width: 3px;
    &:focus:after {
      border-radius: 9999px;
      border-width: 2px;
    }

    width: ${(p) => (p.$arrowsSize === "large" ? 48 : p.$arrowsSize === "medium" ? 40 : 32)}px;
    height: ${(p) => (p.$arrowsSize === "large" ? 48 : p.$arrowsSize === "medium" ? 40 : 32)}px;

    .iconOnly {
      img {
        width: ${(p) => (p.$arrowsSize === "large" ? 24 : 16)}px;
        height: ${(p) => (p.$arrowsSize === "large" ? 24 : 16)}px;
      }
    }

    &.right {
      .iconOnly {
        img {
          transform: rotateZ(180deg);
        }
      }
    }
  }
`;

export default Carousel;
